import type { DropTarget, Key } from '@react-types/shared';
import { findIndex, partition } from 'lodash';

/** First occurrence wins; preserves order. */
export function uniqueOrderedKeys<TKey extends Key>(
  keys: Iterable<TKey>
): TKey[] {
  const seen = new Set<string>();
  const result: TKey[] = [];
  for (const key of keys) {
    const normalized = String(key);
    if (seen.has(normalized)) {
      continue;
    }
    seen.add(normalized);
    result.push(key);
  }
  return result;
}

export function areEqualKeys<TKey extends Key>(a: TKey[], b: TKey[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  return a.every((value, index) => value === b[index]);
}

function moveKeysRelative<TKey extends Key>(
  items: TKey[],
  targetKey: TKey,
  keys: Iterable<Key>,
  position: 'before' | 'after'
): TKey[] {
  const movingSet = new Set(Array.from(keys, String));
  const [movingInOrder, rest] = partition(items, id =>
    movingSet.has(String(id))
  );
  if (movingInOrder.length === 0) {
    return items;
  }
  const index = findIndex(rest, id => String(id) === String(targetKey));
  if (index === -1) {
    return items;
  }
  const insertAt = position === 'before' ? index : index + 1;
  return [
    ...rest.slice(0, insertAt),
    ...movingInOrder,
    ...rest.slice(insertAt)
  ];
}

export function moveKeysBefore<TKey extends Key>(
  items: TKey[],
  targetKey: TKey,
  keys: Iterable<Key>
): TKey[] {
  return moveKeysRelative(items, targetKey, keys, 'before');
}

export function moveKeysAfter<TKey extends Key>(
  items: TKey[],
  targetKey: TKey,
  keys: Iterable<Key>
): TKey[] {
  return moveKeysRelative(items, targetKey, keys, 'after');
}

/** Pairs `CollectionDropIndicator` margin with Tailwind `gap-*` on a flex column list. */
export const dndListGapClassName = {
  '2': '-mb-2',
  '3': '-mb-3',
  '4': '-mb-4'
} as const;

/** Centers the drop line in the item gap (pairs with `gap-N` / `-mb-N` / item `mb-N`). */
export const dndListGapLineClassName = {
  '2': '-top-1 -translate-y-1/2',
  '3': '-top-1.5 -translate-y-1/2',
  '4': '-top-2 -translate-y-1/2'
} as const;

/** Leading list edge — no gap above the first `before` slot (use with list `pt-N` or alone). */
export const dndListGapLineLeadingClassName = 'top-0 -translate-y-1/2';

export type DndListGap = keyof typeof dndListGapClassName;

export function resolveDndListGapLineClassName(
  gap: DndListGap,
  target: DropTarget,
  options?: { firstKey?: Key; hasLeadingPadding?: boolean }
): string {
  if (options?.hasLeadingPadding) {
    return dndListGapLineClassName[gap];
  }

  if (
    options?.firstKey != null &&
    target.type === 'item' &&
    target.dropPosition === 'before' &&
    String(target.key) === String(options.firstKey)
  ) {
    return dndListGapLineLeadingClassName;
  }

  return dndListGapLineClassName[gap];
}
